import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@hooks/useAuth";
import { fetchMFAPreference } from "aws-amplify/auth";
import Spinner from "@components/Spinner";

interface RequireAuthProps {
    children: ReactNode;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
    const { status } = useAuth(); // boolean | undefined
    const location = useLocation();

    const [checkingMfa, setCheckingMfa] = useState(true);
    const [mfaRequired, setMfaRequired] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const checkMfa = async () => {
            if (!status) {
                if (!cancelled) {
                    setCheckingMfa(false);
                    setMfaRequired(false);
                }
                return;
            }

            if (!cancelled) {
                setCheckingMfa(true);
                setMfaRequired(false);
            }

            try {
                const mfa = await fetchMFAPreference();

                const hasTotp =
                    mfa?.preferred === "TOTP" || mfa?.enabled?.includes("TOTP");

                if (!cancelled) {
                    setMfaRequired(!hasTotp);
                }
            } catch {
                // fallback allow
                if (!cancelled) setMfaRequired(false);
            } finally {
                if (!cancelled) setCheckingMfa(false);
            }
        };

        checkMfa();

        return () => {
            cancelled = true;
        };
    }, [status, location.pathname]);

    if (status === undefined || checkingMfa) {
        return (
            <div className="flex items-center justify-center fixed inset-0 z-50 bg-primary-10">
                <Spinner size="lg" />
            </div>
        );
    }

    if (!status) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    if (mfaRequired && location.pathname !== "/setup-mfa") {
        return <Navigate to="/setup-mfa" replace />;
    }

    if (!mfaRequired && location.pathname === "/setup-mfa") {
        return <Navigate to="/demo-lab" replace />;
    }

    return <>{children}</>;
};

export default RequireAuth;